import { IRowData } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";
import { ExtendedPropertiesType } from "UI/types";
import { AdditionalOrdering, IDataSource, IDataSourceLoadInfo, IEditableDataSource, IOrderBy, OrderType } from "../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent } from "../../Global/Base/Events";
import { IDisposable } from "../../Global/Base/IDisposable";
import { IAwaitableEvent } from "../UI.BaseControls/AwaitableEvent";
import { IValidatable, IValidationResult } from "../UI.BaseControls/ControlInterfaces";
import { ICustomElement } from "../UI.BaseControls/ICustomElement";
import { IBooleanDetailValueEvent } from "../UI.BaseControls/IEvent";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { IDataRowElement, IEditableListDataRowElement, IListDataRowElement } from "./DataGridSubcontrols/Components/IDataRow";
import { IHeaderRow } from "./DataGridSubcontrols/Components/IHeaderRow";
import { IEmptyDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { IPermissionChanges } from "./EditableDatagrid";
import {
    ICellValueChangedPrewAwaitableEvent,
    IColumnOrderingChangedEvent,
    IColumnPositionChangedEvent,
    IColumnPositionChangedEventData,
    IDataGridDataShownEvent,
    IDataGridSelectionChangedEvent,
} from "./IEvent";

/**
 * Základní rozhraní pro DataGrid, který nepředpokládá nic o svém rozložení ani způsobu zobrazení dat
 * @includeToDoc
 */
export interface IDataGrid<R extends IDataRowElement> extends IDisposable, HTMLElement, ICustomElement {
    /**
     * Zda je grid v režimu pouze pro čtení
     */
    interactive: boolean;
    /**
     * Obsahuje všechny načtené řádky datagridu
     */
    dataRows: R[];
    /**
     * Seznam všech vybraných řádků
     */
    selectedRows: R[];
    /**
     * Poslední vybraný záznam
     */
    lastClickedRow: R;
    /**
     * Název třídy zobrazované v datagridu
     */
    objectName: string;
    /**
     * Řídí, zda se má vykreslovat hlavička gridu
     */
    renderHeader: boolean;
    /**
     * Hlavička gridu
     */
    headerRow: IHeaderRow<R>;
    /**
     * Volitelná barva hlavičky gridu
     */
    headerColor?: string;
    /**
     * Zdroj dat datagridu
     */
    dataSource: IDataSource;
    /**
     * Seznam sloupců datagridu
     */
    columns: Column[];
    /**
     * Povoluje možnost výběru více záznamů najednou
     */
    multiSelect: boolean;
    /**
     * Informace zda jsou vybrány všechny řádky (i ty ještě nezobrazené)
     */
    isSelectedAll: boolean;
    /**
     * Zda má být automaticky vybrán první záznam
     */
    autoSelectFirstRecord: boolean;
    /**
     * Vybere první řádek pouze pokud je načtený jen jeden záznam
     */
    autoSelectOnlyIfOneRow: boolean;
    /**
     * Informace zda je element viditelný (a tím pádem zda má cenu renderovat obsah)
     */
    readonly canValueLoad: boolean;
    /**
     * Informace zda už proběhla inicializace gridu
     */
    readonly initialized: boolean;
    /**
     * Flag whether all data has been loaded.
     */
    readonly allDataRead: boolean;
    /**
     * Řídí zda se mají vykreslovat ovládací prvky pro záznam (tři tečky)
     */
    renderControlElement: boolean;
    /**
     * Počet záznamů, které se mají načítat v jedné dávce
     * TODO: presunout na DataSource
     */
    fetchCount: number;
    /**
     * Počáteční index prvku pro získání dat z datasource
     */
    startIndex: number;
    /**
     * Počet přeskočených prvků při přidávání řádků do datarows
     */
    skippedRowsCount: number;
    // TODO: odstranit z rozhraní v https://tfs/tfs/Jadro/Jadro%20.NET/_workitems?id=16682
    keyPressRowIndex: number; // index radku pri pohybu sipkami up/down + shift

    /**
     * Vrátí vybrané dataKey řádků, pokud je výběr složitější vrací je pomocí podmínky jinak vrátí pole dataKeys
     * @param forceCondition pokud je true, vždy vrací podmínku, výchozi false
     */
    getSelectionData(forceCondition?: boolean): string | string[];

    /**
     * Vloží nový element reprezentující existující záznam a vrátí ho
     * @param dataRow Data nového záznamu
     */
    insertExistingRecord(dataRow: IRowData): R;

    /**
     * Vytvoří a vloží nový element reprezentující nový záznam a vrátí ho
     * @param dataRow Data nového záznamu
     */
    insertNewRecord(dataRow: IRowData): R;
    /**
     * Odstraní konkrétní řádek z datagridu
     * @param dataKey Identifikátor záznamu
     */
    removeRow(dataKey: string): Promise<void>;
    /**
     * Odstraní všechny vybrané řádky
     */
    removeSelectedRows(): void;
    /**
     * Zruší označení předaného záznmu
     * @param dataKey Identifikátor záznamu
     */
    deselectRow(dataKey: string): void;
    /**
     * Odstraní všechny řádky datagridu
     */
    clearDataRows(): void;
    /**
     * Označí konkrétní řádek, pokud je načtený a vrátí ho
     * @param dataKey Identifikátor záznamu
     */
    selectRow(dataKey: string): Promise<R>;
    /**
     * Vybere všechny řádky
     */
    selectAllRows(): void;
    /**
     * Zruší označení všech řádků
     * @param triggerSelectionChanged Zda se má oznámit změna výběru
     */
    clearSelectedRows(triggerSelectionChanged?: boolean): void;
    /**
     * Zjistí, zda-li dle dostupných informací má být režim {@link isSelectedAll} vypnutý - a pokud ano, tak jej vypne.
     * (K lednu 2021 to je stav, kdy je vybrán nejvýše jeden řádek.)
     */
    clearIsSelectedAllIfNeeded(): void;

    /**
     * Označí předchozí záznam
     * @param trigger Zda se má oznámit změna výběru
     */
    prevRecord(trigger?: boolean): void;
    /**
     * Označí následující záznam
     * @param trigger Zda se má oznámit změna výběru
     */
    nextRecord(trigger?: boolean): void;
    /**
     * Znovu načte a vykreslí data
     */
    refreshData(): Promise<void>;
    /**
     * Znovu vykreslí obsah
     */
    reRenderData(): Promise<void>;
    /**
     * Oznamuje gridu, aby si přepočítal rozměry a případně dočetl další data
     * @param eventData Informace o načtení nových dat
     */
    resizeGrid(eventData?: IDataSourceLoadInfo): void;

    /**
     * Obnoví dočítání dat, pokud bylo pozastaveno
     */
    resumeLoading(): void;

    /**
     * Zaregistruje tovární metodu, pomocí které se budou vytvářet elementy řádků
     * @param factory Tovární metoda vytvářející elementy řádku
     */
    registerDataRowFactory(factory: (dataGrid: IDataGrid<R>, data: IRowData) => R): void;

    /**
     * Umožňuje zaregistrovat vlastní tovární funkce, která se používá při vytváření ovládacích prvků pro každý řádek
     * @param factory Tovární funkce
     */
    registerRowControlsFactory(factory: (dataKeyFetch: () => string) => HTMLElement): void;

    /**
     * Nastaví používané filtry
     * @param filters Kolekce aktivních filtrů
     */
    setFilters(filters: { attrName: string; filterElement: HTMLSpanElement }[]): void;

    /**
     * Nastaví obsah pro patičku. Je zodpovědnost obsahu nastavit si správnou šířku a barvu pozadí, aby se správně překrýval obsah tabulky při scrollování.
     * @param content
     */
    setFooterContent(content: HTMLElement): void;

    /**
     * Nastaví, zda se má vykreslovat patička.
     * @param renderFooter
     */
    setRenderFooter(renderFooter: boolean): void;

    /**
     * Vrací dodatečné vlastnosti daného atributu
     * @param attrName Jméno atributu, jehož vlastnosti se vracejí
     * @returns Dodatečné vlastnosti
     */
    getExtendedProperties?(attrName: string): ExtendedPropertiesType;

    /**
     * Nastaví dodatečné vlastnosti atributů na gridu
     * @param extendedProperties Dodatečné vlastnosti atributů na gridu
     */
    setExtendedProperties?(extendedProperties: CaseInsensitiveMap<ExtendedPropertiesType>): void;

    registerEventListener(type: "SelectionChanged", listener: (ev: IDataGridSelectionChangedEvent) => void, customId?: string): void;
    registerEventListener(type: "SelectionChangedPreview", listener: (ev: IAwaitableEvent<boolean>) => void, customId?: string): void;
    registerEventListener(type: "SelectionAll", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "CellValueChangedPreview", listener: (ev: ICellValueChangedPrewAwaitableEvent) => void, customId?: string): void;
    registerEventListener(type: "Attached", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "DataShown", listener: (ev: IDataGridDataShownEvent) => void, customId?: string): void;
    registerEventListener(type: "ReadOnlyChange", listener: (ev: IBooleanDetailValueEvent) => void, customId?: string): void;
    registerEventListener(type: "BeforeOrder", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "OrderingChanged", listener: (ev: IColumnOrderingChangedEvent) => void, customId?: string): void;
    registerEventListener(type: "ColumnPositionChanged", listener: (ev: IColumnPositionChangedEvent) => void, customId?: string): void;
    registerEventListener(type: string, listener: EventListener, customId?: string): void;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export abstract class IDataGrid<R extends IDataRowElement> {
    private static _properties: (keyof (IDataGrid<IDataRowElement>))[] = ["interactive", "dataRows", "selectedRows", "lastClickedRow", "objectName", "renderHeader", "headerRow", "headerColor",
        "dataSource", "columns", "multiSelect", "isSelectedAll", "autoSelectFirstRecord", "autoSelectOnlyIfOneRow", "canValueLoad", "initialized", "renderControlElement",
        "fetchCount", "keyPressRowIndex"];
    private static _methods: (keyof (IDataGrid<IDataRowElement>))[] = ["insertNewRecord", "removeRow", "removeSelectedRows", "deselectRow", "clearDataRows", "selectRow", "clearSelectedRows",
        "prevRecord", "nextRecord", "refreshData", "reRenderData", "resizeGrid", "resumeLoading", "registerDataRowFactory", "registerRowControlsFactory", "setFilters", "selectAllRows"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IDisposable && instance instanceof HTMLElement;
    }
}

/**
 * Základní rozhraní pro DataGrid, který nepředpokládá nic o svém rozložení ani způsobu zobrazení dat, ale již může zobrazovat blok s informacemi o dostupnosti dat
 * @includeToDoc
 */
export interface IEmptyMessageDataGrid<R extends IDataRowElement> extends IDataGrid<R> {
    /** Element zobrazující hlášky v prázdném gridu */
    emptyMessage: IEmptyDataGridMessage;
    /** Nastavuje DataGrid do režimu QueryMode, kdy se nezobrazují žádná data dokud uživatel neprovede akci */
    setToQueryMode(dataLoad: Function): void;
    /** Ověřuje, zda je DataGrid v režimu QueryMode */
    isInQueryMode(): boolean;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export abstract class IEmptyMessageDataGrid<R extends IDataRowElement> {
    private static _properties: (keyof (IEmptyMessageDataGrid<IDataRowElement>))[] = ["emptyMessage"];
    private static _methods: (keyof (IEmptyMessageDataGrid<IDataRowElement>))[] = ["setToQueryMode", "isInQueryMode"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Základní rozhraní pro seznamový DataGrid, který má řádky a sloupce
 * @includeToDoc
 */
export interface IListDataGrid<R extends IListDataRowElement> extends IEmptyMessageDataGrid<R> {
    /**
     * Událost vyvolána při změně pozice sloupce
     */
    columnPositionChanged: GlobalEvent<IListDataGrid<R>, IColumnPositionChangedEventData>;
    /**
     * Aktualní řazení dataGridu
     */
    orderBy: IOrderBy[];
    /**
     * Nastavuje kolik prvních sloupců má být ukotvených (zmražených, statických)
     */
    frozenColumnCount: number;
    /**
     * Název atributu ze kterého bude z příchozích dat načteno obarvení řádku
     */
    flagAttributeName: string;
    /**
     * Pole barev flagů, které je možno použít pro označení řádku
     * Pokud je undefined, jsou barvy pro flag bez omezení
     */
    allowedFlagColors: string[] | undefined;
    /**
     * Řídí zda se má zobrazovat FrameMenu
     */
    showFrameMenu: boolean;

    /**
     * Změní aktivní řazení gridu
     * @param column Sloupec, pro který se mění řazení
     * @param orderType Směr řazení
     * @param addToExisting Zda se má nové řazení přidat ke stávajícímu nebo ho nahradit
     */
    changeOrdering(column: Column, orderType: OrderType | null, addToExisting: boolean, additionalOrdering?: AdditionalOrdering): Promise<void>;
    /**
     * Hromadně nastaví řazení gridu
     * @param ordering Kolekce atributů a jejich řazení
     */
    setOrderBy(ordering: IOrderBy[], callEvent?: boolean): boolean;

    /**
     * Vybere všechny záznamy v seznamu
     */
    selectAllRows(): void;

    /**
     * Nastaví defaultní třídění
     * Pokud je nastaveno, tak se třídí podle definice formuláře, jinak podle dataKey (nebo ID)
     * @param formDefinitionOrderBy Třídění z definice formuláře
     * @returns Vrací true, pokud bylo třídění změněno. Pokud bylo nastavované stejné jako předchozí, vrací false.
     */
    setDefaultOrderBy(formDefinitionOrderBy: IOrderBy[]): boolean;

    /**
     * Zakáže možnost řazení kliknutím na hlavičku sloupce
     */
    disableSort(): void;

    /**
     * Nascrollování seznamu na vybraný řádek
     */
    scrollToSelectedRow(): void;

    /**
     * Přesune předaný sloupec na novou pozici
     * @param column Přesouvaný sloupec
     * @param newPosition Nová pozice
     */
    setColumnOrder(column: Column, newPosition: number, forced?: boolean): void;
    /**
     * Nastaví šířku sloupce
     * @param column Sloupek, kterému se mění šířka
     * @param width Nová šířka
     */
    setColumnWidth(column: Column, width: number): void;

    /**
     * Aktualizuje šířku prvního sloupce
     */
    resizeHeaderFirstColumn(): void;

    /**
     * Vypočítá šířku buňky s ovládacími prvky včetně marginu
     */
    computeChoiceCellWidth(includeMargin?: boolean): number;

    /**
     * Vykreslí předané sloupce
     * @param columns Sloupce k vykreslení
     * @param orderBy Řazení sloupců
     * @param startIndex Od kterého indexu mají být nové sloupce číslovány
     * @param addNoSortItem - příznak určující, zda se má dorovnávat šířka scrollbaru
     * @param addCells - příznak určující, zda se mají na konec každého řádku přidat buňky nových sloupců
     */
    renderColumns(columns: Column[], orderBy: IOrderBy[], startIndex?: number, addNoSortItem?: boolean, addCells?: boolean): void;

    /**
     * Odstraní z dataGridu všechny dynamické sloupce
     */
    removeDynamicColumns(): void;

    /**
     * Vygeneruje pro sloupce jejich pořadí v seznamu
     */
    generateColumnsOrder(): void;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export abstract class IListDataGrid<R extends IListDataRowElement> {
    private static _properties: (keyof (IListDataGrid<IListDataRowElement>))[] = ["orderBy", "frozenColumnCount", "showFrameMenu"];
    private static _methods: (keyof (IListDataGrid<IListDataRowElement>))[] = ["changeOrdering", "setOrderBy", "setDefaultOrderBy", "disableSort", "scrollToSelectedRow", "setColumnOrder", "setColumnWidth", "resizeHeaderFirstColumn", "renderColumns", "selectAllRows",
        "removeDynamicColumns", "generateColumnsOrder"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IDataGrid;
    }
}

/**
 * Základni rozhraní pro editovatelný seznamový DataGrid, kde lze editovat jednotlivé buňky
 * @includeToDoc
 */
export interface IEditableListDataGrid<R extends IEditableListDataRowElement> extends IListDataGrid<R> {
    /**
     * Událost vyvolaná při změně validnosti buňky gridu
     */
    readonly cellValidityChanged: GlobalEvent<IValidatable, { row: R; validity: IValidationResult }>;
    /**
     * Událost vyvolaná při vyhodnocení práv
     */
    readonly permissionsChanged: GlobalEvent<R, IPermissionChanges[]>;

    /**
     * Informace zda je povolena multieditace
     */
    isMultiEditAllowed: boolean;

    /**
     * Informace zda je datagrid v editačním režimu
     */
    isInEdit: boolean;
    /**
     * Zdroj dat datagridu
     */
    dataSource: IEditableDataSource;
    /**
     * Upravené, neuložené řádky
     */
    editedRows: R[];
    /**
     * Povoluje/zakazuje zobrazování řádku s počtem rozeditovaných záznamů
     */
    displayEditedCountBar: boolean;
    /**
     * Určuje zda jsou právě zobrazeny jenom rozeditované záznamy
     */
    displayedEditedRows: boolean;
    /**
     * Příznak, že se při aktualizaci dat nemají zachovávat záznamy, které jsou označené nebo změněné, pokud nejsou v nové dávce dat
     */
    dontKeepChangedRecords: boolean;
    /**
     * Callback umožňující řízení, zda je možné přepnout grid do editace
     */
    canSwitchToEditCallback?: () => boolean;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export abstract class IEditableListDataGrid<R extends IEditableListDataRowElement> {
    private static _properties: (keyof (IEditableListDataGrid<IEditableListDataRowElement>))[] = ["cellValidityChanged", "permissionsChanged", "isInEdit", "dataSource", "editedRows", "displayEditedCountBar"];
    private static _methods: (keyof (IEditableListDataGrid<IEditableListDataRowElement>))[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IListDataGrid;
    }
}
